import { ProgressSpinner } from "primereact/progressspinner";
import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Http from "../../../Common/Http";
const Create = ({ setVisible, fetchData }) => {
  const [inputs, setInputs] = useState({
    title: "",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    description: "",
    reference_links: [],
    server_credentials: [],
    important_documents: [],
  });
  const [visibleFields, setVisibleFields] = useState([]); // Track which fields are visible
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleQuillChange = (value) => {
    setInputs((prevState) => ({ ...prevState, description: value }));
    // Clear error when field is filled
    if (value && errors.description) {
      setErrors((prev) => ({ ...prev, description: null }));
    }
  };

  const handleReferenceLinksChange = (e, index, property) => {
    const value = e.target.value;
    setInputs((prevState) => {
      const updatedLinks = [...prevState.reference_links];
      // If the current item doesn't exist or isn't an object, initialize it
      if (!updatedLinks[index] || typeof updatedLinks[index] !== "object") {
        updatedLinks[index] = { title: "", url: "" };
      }
      // Update the specific property (title or url)
      updatedLinks[index] = { ...updatedLinks[index], [property]: value };
      return { ...prevState, reference_links: updatedLinks };
    });
  };

  const handleServerCredentialsChange = (e, index, property) => {
    const value = e.target.value;
    setInputs((prevState) => {
      const updatedCreds = [...prevState.server_credentials];
      // If the current item doesn't exist or isn't an object, initialize it
      if (!updatedCreds[index] || typeof updatedCreds[index] !== "object") {
        updatedCreds[index] = { server_name: "", url: "", username: "", password: "", additional_info: "" };
      }
      // Update the specific property
      updatedCreds[index] = { ...updatedCreds[index], [property]: value };
      return { ...prevState, server_credentials: updatedCreds };
    });
  };

  const handleChange = (e, index, field) => {
    const value = e.target.value;
    setInputs((prevState) => {
      const updatedField = [...prevState[field]];
      updatedField[index] = value;
      return { ...prevState, [field]: updatedField };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prevState) => ({ ...prevState, [name]: value }));

    // Clear error when field is filled
    if (value && errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleAddField = (field) => {
    if (!visibleFields.includes(field)) {
      setVisibleFields([...visibleFields, field]);

      // Initialize with one empty entry when the field is first added
      if (field === "reference_links") {
        setInputs((prevState) => ({
          ...prevState,
          reference_links: [...prevState.reference_links, { title: "", url: "" }],
        }));
      } else if (field === "server_credentials") {
        setInputs((prevState) => ({
          ...prevState,
          server_credentials: [...prevState.server_credentials, { server_name: "", url: "", username: "", password: "", additional_info: "" }],
        }));
      } else if (field === "important_documents") {
        setInputs((prevState) => ({
          ...prevState,
          important_documents: [...prevState.important_documents, ""],
        }));
      }
    }
  };

  const handleAddMore = (field) => {
    if (field === "reference_links") {
      setInputs((prevState) => ({
        ...prevState,
        reference_links: [...prevState.reference_links, { title: "", url: "" }],
      }));
    } else if (field === "server_credentials") {
      setInputs((prevState) => ({
        ...prevState,
        server_credentials: [...prevState.server_credentials, { server_name: "", url: "", username: "", password: "", additional_info: "" }],
      }));
    } else {
      setInputs((prevState) => ({
        ...prevState,
        [field]: [...prevState[field], ""],
      }));
    }
  };

  const handleRemoveField = (field, index) => {
    setInputs((prevState) => {
      const updatedField = [...prevState[field]];
      updatedField.splice(index, 1);
      return { ...prevState, [field]: updatedField };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Check required fields
    if (!inputs.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!inputs.description || inputs.description === "<p><br></p>") {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const res = await Http.post(`/project/store`, inputs);
      const data = await res.data;

      // Instead of refreshing the entire list, we add the new project to the list
      if (data && data.data) {
        fetchData();
        setVisible(false);
      }
    } catch (error) {
      console.error("Error saving project:", error);

      // Handle validation errors from backend
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: "An error occurred while saving the project" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    formSubmit(e);
  };

  return (
    <div>
      {loading ? (
        <ProgressSpinner />
      ) : (
        <div className="container">
          {/* Display general error if exists */}
          {errors.general && (
            <div className="alert alert-danger mb-3" role="alert">
              {errors.general}
            </div>
          )}

          {/* Basic Fields */}
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Title */}
              <div className="col-lg">
                <label>
                  Title <i className="fa fa-star required"></i>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  name="title"
                  placeholder="Enter Project Title"
                  value={inputs.title}
                  required
                  onChange={handleInputChange}
                />
                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              </div>
              {/* Start Date */}
              <div className="col-lg">
                <label>Start Date</label>
                <input
                  type="date"
                  className={`form-control ${errors.start_date ? "is-invalid" : ""}`}
                  name="start_date"
                  value={inputs.start_date}
                  required
                  onChange={handleInputChange}
                />
                {errors.start_date && <div className="invalid-feedback">{errors.start_date}</div>}
              </div>
              {/* End Date */}
              <div className="col-lg">
                <label>End Date</label>
                <input
                  type="date"
                  className={`form-control ${errors.end_date ? "is-invalid" : ""}`}
                  name="end_date"
                  value={inputs.end_date}
                  onChange={handleInputChange}
                />
                {errors.end_date && <div className="invalid-feedback">{errors.end_date}</div>}
              </div>
            </div>

            <div className="row mt-3">
              {/* Description */}
              <div className="col-lg">
                <label>
                  Description <i className="fa fa-star required"></i>
                </label>
                {/* React Quill Component */}
                <div className={errors.description ? "quill-error" : ""}>
                  <ReactQuill
                    theme="snow"
                    value={inputs.description}
                    onChange={handleQuillChange}
                    placeholder="Enter Project Description"
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        ["bold", "italic", "underline"],
                        [{ color: [] }, { background: [] }],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["link"],
                        ["clean"],
                      ],
                    }}
                    formats={["header", "bold", "italic", "underline", "color", "background", "list", "bullet", "link"]}
                    style={{ height: "150px", marginBottom: "40px" }}
                  />
                </div>
                {errors.description && <div className="text-danger mt-n4 mb-2">{errors.description}</div>}
              </div>
            </div>

            <div className="row mt-3">
              {/* File Upload */}
              <div className="col-lg">
                <label>File Upload</label>
                <input type="file" className={`form-control ${errors.documents ? "is-invalid" : ""}`} name="documents" multiple />
                {errors.documents && <div className="invalid-feedback">{errors.documents}</div>}
                <small className="text-muted">Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG</small>
              </div>
            </div>

            {/* Dropdown for additional fields */}
            <div className="row mt-3">
              {/* Dropdown */}
              <div className="col-lg">
                <div className="dropdown">
                  <button
                    className="btn btn-secondary dropdown-toggle"
                    type="button"
                    id="dropdownMenuButton"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Add Fields
                  </button>
                  <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                    {/* Reference Links */}
                    <li>
                      <button className="dropdown-item" type="button" onClick={() => handleAddField("reference_links")}>
                        Reference Links
                      </button>
                    </li>
                    {/* Server Credentials */}
                    <li>
                      <button className="dropdown-item" type="button" onClick={() => handleAddField("server_credentials")}>
                        Server Credentials
                      </button>
                    </li>
                    {/* Important Documents */}
                    <li>
                      <button className="dropdown-item" type="button" onClick={() => handleAddField("important_documents")}>
                        Important Documents
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Reference Links */}
            {visibleFields.includes("reference_links") && (
              <div className="row mt-4">
                <div className="col-lg-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6>Reference Links</h6>
                    <button className="btn btn-secondary btn-sm ms-1" type="button" onClick={() => handleAddMore("reference_links")}>
                      <i className="fa fa-plus"></i>
                    </button>
                  </div>
                  {errors.reference_links && <div className="text-danger mb-2">{errors.reference_links}</div>}
                  {inputs.reference_links.map((link, index) => (
                    <div className="d-flex align-items-center my-2" key={`reference_${index}`}>
                      {/* Title */}
                      <input
                        type="text"
                        className="form-control me-2"
                        value={link.title || ""}
                        placeholder={`Title ${index + 1}`}
                        style={{ width: "50%" }}
                        onChange={(e) => handleReferenceLinksChange(e, index, "title")}
                      />
                      {/* Reference Link */}
                      <input
                        type="text"
                        className="form-control me-2"
                        value={link.url || ""}
                        placeholder={`URL ${index + 1}`}
                        onChange={(e) => handleReferenceLinksChange(e, index, "url")}
                      />
                      <button className="btn btn-danger btn-sm" type="button" onClick={() => handleRemoveField("reference_links", index)}>
                        <i className="fa fa-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Server Credentials */}
            {visibleFields.includes("server_credentials") && (
              <div className="row mt-4">
                <div className="col-lg-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6>Server Credentials</h6>
                    <button className="btn btn-secondary btn-sm ms-2" type="button" onClick={() => handleAddMore("server_credentials")}>
                      <i className="fa fa-plus"></i>
                    </button>
                  </div>
                  {errors.server_credentials && <div className="text-danger mb-2">{errors.server_credentials}</div>}
                  {inputs.server_credentials.map((cred, index) => (
                    <div className="my-2" key={`server_${index}`}>
                      <div className="d-flex align-items-center mb-2">
                        {/* Server Name */}
                        <input
                          type="text"
                          className="form-control me-2"
                          value={cred.server_name || ""}
                          placeholder={`Server Name ${index + 1}`}
                          onChange={(e) => handleServerCredentialsChange(e, index, "server_name")}
                        />
                        {/* URL */}
                        <input
                          type="text"
                          className="form-control me-2"
                          value={cred.url || ""}
                          placeholder={`URL ${index + 1}`}
                          onChange={(e) => handleServerCredentialsChange(e, index, "url")}
                        />
                        <button className="btn btn-danger btn-sm" type="button" onClick={() => handleRemoveField("server_credentials", index)}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>

                      {/* Username and Password Fields */}
                      <div className="d-flex mb-2">
                        <input
                          type="text"
                          className="form-control me-2"
                          value={cred.username || ""}
                          placeholder={`Username ${index + 1}`}
                          onChange={(e) => handleServerCredentialsChange(e, index, "username")}
                        />
                        <input
                          type="password"
                          className="form-control"
                          value={cred.password || ""}
                          placeholder={`Password ${index + 1}`}
                          onChange={(e) => handleServerCredentialsChange(e, index, "password")}
                        />
                      </div>

                      {/* Additional Info */}
                      <div className="d-flex">
                        <input
                          type="text"
                          className="form-control"
                          value={cred.additional_info || ""}
                          placeholder={`Additional Info ${index + 1}`}
                          onChange={(e) => handleServerCredentialsChange(e, index, "additional_info")}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Important Documents */}
            {visibleFields.includes("important_documents") && (
              <div className="row mt-4">
                <div className="col-lg-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6>Important Documents</h6>
                    <button className="btn btn-secondary btn-sm ms-2" type="button" onClick={() => handleAddMore("important_documents")}>
                      <i className="fa fa-plus"></i>
                    </button>
                  </div>
                  {errors.important_documents && <div className="text-danger mb-2">{errors.important_documents}</div>}
                  {inputs.important_documents.map((doc, index) => (
                    <div className="my-2" key={`document_${index}`}>
                      <div className="d-flex align-items-center">
                        <input
                          type="file"
                          className="form-control me-2"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            setInputs((prevState) => {
                              const updatedDocuments = [...prevState.important_documents];
                              updatedDocuments[index] = file;
                              return {
                                ...prevState,
                                important_documents: updatedDocuments,
                              };
                            });
                          }}
                        />
                        <button className="btn btn-danger btn-sm" type="button" onClick={() => handleRemoveField("important_documents", index)}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                      {doc && doc.name && (
                        <small className="text-muted">
                          Selected file: {doc.name} ({(doc.size / 1024).toFixed(2)} KB)
                        </small>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="row mt-4">
              <div className="col-lg">
                <button className="btn btn-primary float-end" type="submit">
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
      <style>{`
        .quill-error .ql-container {
          border-color: #dc3545;
        }
        .quill-error .ql-toolbar {
          border-color: #dc3545;
        }
      `}</style>
    </div>
  );
};

export default Create;
